import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useAuthStore } from './auth-store'
import { useShopStore } from './shop-store'

/**
 * User Store (View Model)
 *
 * This is a view model store that aggregates data from:
 * - shopStore: shop-related data (shop details, install status, etc.)
 * - authStore: user authentication data (user email, etc.)
 *
 * It provides computed properties for UI components without owning the underlying state.
 */
export const useUserStore = defineStore('user', () => {
  const authStore = useAuthStore()
  const shopStore = useShopStore()

  // User-related computed properties (from shop or auth)
  const userName = computed(() => {
    return shopStore.shopDetails?.shop_owner || authStore.user?.email || 'User'
  })

  const email = computed(() => {
    return shopStore.shopDetails?.email || authStore.user?.email || ''
  })

  // Shop-related computed properties (from shopStore only)
  const shopName = computed(() => {
    return shopStore.shopDetails?.name || ''
  })

  const domain = computed(() => {
    const s = shopStore.shopDetails
    return s?.domain || s?.myshopify_domain || ''
  })

  const memberSince = computed(() => {
    const s = shopStore.shopDetails
    const raw = s?.install_date || s?.created_at
    if (!raw) return ''

    try {
      const date = new Date(raw)
      // Use undefined for locale to respect browser/i18n settings
      return date.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return ''
    }
  })

  const country = computed(() => {
    const s = shopStore.shopDetails
    return s?.country_name || s?.country || ''
  })

  const currency = computed(() => {
    return shopStore.shopDetails?.currency || 'USD'
  })

  const moneyFormat = computed(() => {
    return shopStore.shopDetails?.money_format || '{{ amount }}'
  })

  const moneyWithCurrencyFormat = computed(() => {
    return shopStore.shopDetails?.money_with_currency_format || `{{ amount }} ${currency.value}`
  })

  const timezone = computed(() => {
    const s = shopStore.shopDetails
    return s?.timezone || s?.iana_timezone || ''
  })

  const planName = computed(() => {
    const s = shopStore.shopDetails
    return s?.plan_display_name || s?.plan_name || ''
  })

  const installStatus = computed(() => {
    return shopStore.shopDetails?.install_status || ''
  })

  const scopes = computed(() => {
    return shopStore.shopDetails?.scopes || []
  })

  const crispId = computed(() => {
    return shopStore.shopDetails?.crisp_id || ''
  })

  // UI-only defaults (consider moving these to a UI store in the future)
  const pfp = 'https://picsum.photos/id/22/200/300'
  const is2FAEnabled = false

  // Placeholder actions (to be implemented when needed)
  const toggle2FA = () => {
    // Placeholder: In a real app, this would make an API call
  }

  const changeUserName = (_newUserName: string) => {
    // Placeholder: In a real app, this would update the shop owner name via API
  }

  return {
    // User-related computed properties
    userName,
    email,

    // Shop-related computed properties
    shopName,
    domain,
    memberSince,
    country,
    currency,
    moneyFormat,
    moneyWithCurrencyFormat,
    timezone,
    planName,
    installStatus,
    scopes,
    crispId,

    // UI-only static properties
    pfp,
    is2FAEnabled,

    // Placeholder actions
    toggle2FA,
    changeUserName,
  }
})
